import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import SmartChart from '../../../../components/Chart/SmartChart';
import Skeleton from '../../../../components/Skeleton';
import ChartLegend from '../ChartLegend';
import useChartLegend from '../../hooks/useChartLegend';

import { nodes, segmentTransitionsConfig } from './config';

const SankeyChart = ({ data, selectedDate, withEndSeries = false, settings, footerRight }) => {
  const initSeries = data?.chart_series || [];

  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut, classesToAdd } =
    useChartLegend();
  const sourceIds = new Set(initSeries.map((item) => item[0]));
  const destinationIds = new Set(initSeries.map((item) => item[1]));
  const churnedNames = new Set(['End-Churned App Usage', 'End-Churned AI']);

  const legendNodes = nodes.filter((node) => {
    if (withEndSeries && churnedNames.has(node.name) && destinationIds.has(node.name)) return true;
    if (sourceIds.has(node.id)) return true;
    if (node.name.includes('End-')) return false;
    return false;
  });

  const legendNodeIds = legendNodes.map((node) => node.id);

  const hoveredLegendNode = legendNodes.find((node) => node.id === hoveredItem?.id);

  const activeNode = legendNodes.find(
    (node) => node.id === activeItem?.id || node.id === hoveredItem?.id
  );

  const shouldAddClass = !!activeNode || !!hoveredLegendNode;

  const nextNodes = nodes.map((node) => {
    const className = classesToAdd(node.id);
    return {
      ...node,
      className: `
        ${shouldAddClass ? className : ''}
        ${node.id.includes('End-') ? 'to-node' : ''}
        ${activeItem?.id?.includes('End-') && !node.id.includes('End-') ? 'from-node' : ''}`,
    };
  });
  const nextSeries = initSeries?.map((item) => {
    const className = classesToAdd(
      (!activeItem?.id && hoveredItem?.id?.includes('End-')) || activeItem?.id?.includes('End-')
        ? item[1]
        : item[0]
    );

    return [...item, shouldAddClass ? className : ''];
  });

  const config = segmentTransitionsConfig({
    selectedDate,
    legendNodeIds,
    activeItem,
    hoveredItem,
    hasData: !!initSeries.length,
    onClick,
    onMouseOver,
  });

  const metaSeriesData = {
    sankey: {
      type: 'sankey',
      borderRadius: 0,
      nodeWidth: 16,
      colorByPoint: true,
      clip: false,
      visible: true,
      x: -8,
      keys: ['from', 'to', 'weight', 'className'],
      nodes: nextNodes,
    },
  };

  if (settings.pending) return <Skeleton height={392} />;

  return (
    <div
      className={cx('chart-container segment-transitions__chart', {
        'has-active-item': activeItem?.id,
        'has-hovered-item': hoveredItem?.id && !activeItem?.id,
      })}
    >
      <div onMouseOut={onMouseOut} onBlur={onMouseOut}>
        <SmartChart
          chartId="segment-transitions__chart"
          config={config}
          series={{ sankey: nextSeries }}
          metaSeriesData={metaSeriesData}
        />
      </div>
      <div className="d-flex align-items-center justify-content-between gap-16">
        <ChartLegend
          series={legendNodes}
          activeItem={
            activeItem?.id || (!activeItem?.id && !hoveredItem?.to ? hoveredItem?.id : undefined)
          }
          onClick={onClick}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        />
        {footerRight}
      </div>
    </div>
  );
};

SankeyChart.propTypes = {
  settings: T.shape({
    pending: T.bool,
  }).isRequired,
  data: T.shape({
    chart_series: T.array,
  }).isRequired,
  selectedDate: T.string.isRequired,
  withEndSeries: T.bool,
  footerRight: T.node,
};

export default SankeyChart;
